import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEarthAmericas, faEye, faPenToSquare, faUserShield } from '@fortawesome/free-solid-svg-icons';
import { UserAuth } from '../../authentication/AuthContext.jsx';
import { useSelector, useDispatch } from "react-redux";
import {
    updateDashboardContext, setEffectivePermissions, setUserGames, setUserGamesLoaded, setSuspendedGames,
    setRequestableGames, setUserPortfolios, setUserPortfoliosLoaded,
} from "../../redux-store/ReduxStore.js";
import { getEffectivePermissions } from "../../services/admin-settings/UserPermissionService.jsx";
// Note: initial permissions fetch happens in DashboardLayout. This header re-fetches on context changes.
import { ContextSwitcher } from './ContextSwitcher.jsx';
import { SidebarSwitcher } from '../sidebar/SidebarSwitcherSlot.jsx';
import { headerCardsForPath } from '../../routes/navScopes.jsx';
import { useNavScope } from '../sidebar/useNavScope.js';
import { findGameBySlug, gameSlug } from '../../utils/gameSlug.js';
import { gameEntryPath } from '../../routes/onboardingSteps.js';
import { byPortfolioName, findPortfolioBySlug, portfolioSlug, routePortfolioSlug } from '../../routes/portfolioScopeNav.jsx';
import useSelectedGameLifecycle, { LIFECYCLE_CHANGED_EVENT } from '../../hooks/useSelectedGameLifecycle.js';
import { isSelectedGameStatusRelevant, effectiveClientState, GAME_STATE } from '../../utils/gameLifecycle.js';
import { suspendedClientIdsForGame } from '../../utils/membershipAccess.js';
import { detectAccessDrift, logAccessDrift } from '../../utils/accessDriftLogger.js';
import { HeaderActionsSlot } from './HeaderActionsSlot.jsx';
import { NotificationBell } from './NotificationBell.jsx';
import StatusPill from '../../components/status-pill/StatusPill.jsx';
import GameIcon from '../../components/game-icon/GameIcon.jsx';

import { getGameRegistries, getGameRegistriesByOrgIdWithAccessibility } from "../../services/admin-settings/GameService";
import { getOrgById, getOrgNameById, getAllOrgs } from "../../services/admin-settings/org-management/OrgRegistrationService";
import { updateActiveContext } from "../../services/UserProfileService.jsx";
import { getUserGameRegistries } from "../../services/admin-settings/UserGameService";
import { getMyGeographies } from "../../services/players/PlayerSegmentService.jsx";
import { geographyPickerItems, reconcileSelectedSegment, GLOBAL_VALUE, GLOBAL_ITEM } from './headerGeographies.js';
import { gamePageLabel, gamePageScope } from '../../utils/pageScope.js';
import { pageAccessLabel, READ_WRITE } from './pageAccess.js';
import { GAMES_CHANGED_EVENT } from "../../utils/gamesChanged.js";
import { SEGMENTS_CHANGED_EVENT, concernsGame } from "../../utils/segmentsChanged.js";
import { ACCESS_CHANGED_EVENT } from "../../authentication/accessChange.js";
import { PORTFOLIOS_CHANGED_EVENT } from "../../utils/portfoliosChanged.js";
import { listPortfolios } from "../../services/portfolios/UserPortfolioService.jsx";

import { lifecycleStateOf, orgStatusLabel, requestableGames, switcherGames } from "./switcherGames.js";
import { fetchOrgNames, orgDataOf, orgNameRetryDelay } from './orgNames.js';
import { RECONNECTED_EVENT } from '../app-status/appStatus.js';

import './DashboardHeader.css';

// Maps a create/update/clone form route to the home route whose context-chip
// visibility we should mirror. Keep this list explicit rather than relying on
// regex generalizations — a form's "parent" is rarely the URL one segment up,
// and silently showing the wrong chips is worse than missing one.
const FORM_TO_HOME = [
    { match: /^\/live-ops\/ab-testing\/(create|update|clone)-ab-testing/, home: '/live-ops/ab-testing' },
    { match: /^\/live-ops\/remote-config\/(create|update|clone)-remote-config/, home: '/live-ops/remote-config' },
    { match: /^\/live-ops\/content-templates\/(create|update|import)/, home: '/live-ops/content-templates' },
    { match: /^\/players\/segmentation\/(create|update|clone)-segmentation/, home: '/players/segmentation' },
    { match: /^\/admin-settings\/game-mgmt\/(register|update)-game/, home: '/admin-settings/game-mgmt/games' },
    { match: /^\/admin-settings\/game-mgmt\/data-connections\/[^/]+\/add/, home: '/admin-settings/game-mgmt/games' },
];

const getHomePathForForm = (pathname) => {
    for (const { match, home } of FORM_TO_HOME) {
        if (match.test(pathname)) return home;
    }
    return null;
};

// Non-form pages that still lock context: switching org/game/build would desync the loaded entity.
const LOCKED_CONTEXT_PATTERNS = [
    /^\/live-ops\/ab-testing\/live-dashboard\//,
    // Segment Explorer is pinned to a specific test (:id, which belongs to one game/build). Switching
    // org/game/build would desync the loaded test from the header, so lock the switcher like the live
    // dashboard rather than leave it silently showing another scope's test.
    /^\/live-ops\/ab-testing\/segment-explorer\//,
    // The AI (investigative) Workspace is pinned to a specific insight; you can't meaningfully switch
    // scope inside it, so lock the switcher there too (form-page style) rather than leave it stale.
    /^\/ai-recommendations\/ai-workspace/,
];
const isLockedContextPage = (pathname) => LOCKED_CONTEXT_PATTERNS.some((re) => re.test(pathname));

const normalizeId = (value) => (value === null || value === undefined ? null : String(value));
const normalizeRegions = (regions, fallback = []) => {
    const base = Array.isArray(regions) && regions.length > 0 ? regions : fallback;
    return base.map(region => String(region));
};

export const DashboardHeader = ({ dashboardType }) => {
    const { user } = UserAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // Form pages re-use their parent home page's chip visibility, but render
    // the chips read-only — the user sees the context their record will be
    // created in without being able to switch mid-flow.
    const homePagePath = getHomePathForForm(location.pathname);
    const isFormPage = !!homePagePath;
    const effectivePath = homePagePath || location.pathname;

    // Page-based filter visibility (computed against effectivePath so a form
    // and its home page agree on which chips to surface).
    const isGamePage = effectivePath.startsWith('/admin-settings/game-mgmt/games');
    // The games registration LIST (exactly /games) is where you PICK a game, so its game/build chips
    // stay hidden. The Game Workspace (/games/:gameClientId/...) is a chosen game, so it shows the
    // game chip up top (like Game User Management) and offers the client picker in its page header.
    const isGamesListPage = /^\/admin-settings\/game-mgmt\/games\/?$/.test(effectivePath);
    const isGameWorkspace = isGamePage && !isGamesListPage;

    // Account / system pages — Profile (Platform Settings included, as its tab), Releases, Help,
    // Notifications — don't operate on org/game/build context, so the
    // whole switcher is suppressed there. Avoids the user thinking the
    // selection matters for what they're viewing.
    const hideContextSwitcher =
        effectivePath === '/profile' || effectivePath.startsWith('/profile/') ||
        effectivePath === '/releases' || effectivePath.startsWith('/releases/') ||
        effectivePath === '/help' || effectivePath.startsWith('/help/') ||
        effectivePath === '/notifications';

    // No Build chip: the access model's scope tree is ORG > GAME (game absorbed client), so a build
    // selection can't change what the user can reach. The build is still resolved and carried in
    // context for URLs and per-build data — it just isn't a scope the operator picks.
    // See docs/nav-scope-alignment.md.

    const [allGames, setAllGames] = useState([]);
    const [orgNames, setOrgNames] = useState({}); // { orgId: orgName }
    const [orgDataMap, setOrgDataMap] = useState({}); // { orgId: { orgOwner, coOwners } }
    // Super users have no org of their own but must browse ALL orgs — populated from getAllOrgs.
    const [superAllOrgs, setSuperAllOrgs] = useState([]);
    // Bumped to ask again for org names that failed to load (see the org-names effect).
    const [orgNamesRetryTick, setOrgNamesRetryTick] = useState(0);
    const orgNamesAttemptRef = useRef(0);
    const persistTimerRef = useRef(null);
    // Tracks which org the loaded accessibility catalogue belongs to, so an org switch can drop the
    // previous org's games before the refetch lands (prevents stale cross-org games leaking into the
    // switcher / auto-select).
    const catalogOrgRef = useRef(null);

    // `allOrgGamesWithAccess` is the full catalogue for the selected org, each entry tagged with
    // `accessible: boolean` from the server. Requesting access to a game happens on the Games page.
    const [allOrgGamesWithAccess, setAllOrgGamesWithAccess] = useState([]);

    // Read from Redux
    const userId = useSelector(state => state.userDetails.userId);
    const isSuperUser = useSelector(state => state.userDetails.superUser);
    const userGames = useSelector(state => state.userGames);
    const selectedSegmentId = useSelector(state => state.dashboardContext?.selectedSegmentId ?? null);
    const scope = useNavScope();
    const orgs = useSelector(state => state.userDetails.orgs) || [];
    const [userGameRegistries, setUserGameRegistries] = useState([]); // from GET /v1/web/user-game
    const [userGameRegistriesLoaded, setUserGameRegistriesLoaded] = useState(false);
    // Bumped by a GAMES_CHANGED_EVENT (e.g. a game just registered) to force the game-list fetches to
    // re-run, so the switcher includes the new game instead of a stale set (which would otherwise get
    // its selection auto-sanitized back to an existing game when the new game's workspace is opened).
    const [gamesRefreshTick, setGamesRefreshTick] = useState(0);
    // Same, for the portfolio list — bumped by PORTFOLIOS_CHANGED_EVENT (e.g. one was just created).
    const [portfoliosRefreshTick, setPortfoliosRefreshTick] = useState(0);
    const portfolioOrgRef = useRef(null);
    const { selectedOrgId, selectedGameId, selectedGameName, selectedGameClientId } =
        useSelector(state => state.dashboardContext);
    const effectivePermissions = useSelector(state => state.effectivePermissions);
    const userPortfolios = useSelector(state => state.userPortfolios);
    // { wholeGame, geographies } for the selected game; null until loaded (see headerGeographies.js).
    const [myGeographies, setMyGeographies] = useState(null);
    const prevSegmentGameRef = useRef(selectedGameId);

    // Super users browse every org; everyone else only their own. Normalized to the {orgId} shape
    // the switcher/derivations expect.
    const effectiveOrgs = useMemo(() => (
        isSuperUser ? superAllOrgs.map(o => ({ orgId: normalizeId(o.id) })) : orgs
    ), [isSuperUser, superAllOrgs, orgs]);

    const normalizedSelectedOrgId = normalizeId(selectedOrgId);
    const normalizedSelectedGameClientId = normalizeId(selectedGameClientId);

    // Lifecycle of the selected scope — drives the "Suspended"/"Sunset" badge next to the switcher
    // so the frozen state is obvious in the header, above the page banner. Mirrors the backend freeze.
    const { frozen: scopeFrozen, gameState: scopeGameState, clientState: scopeClientState } = useSelectedGameLifecycle();
    const scopeBadge = useMemo(() => {
        if (!scopeFrozen) return null;
        const gameFrozen = scopeGameState && scopeGameState !== 'ACTIVE';
        const stateForBadge = gameFrozen ? scopeGameState : scopeClientState;
        const scopeWord = gameFrozen ? 'Game' : 'Client';
        const isSunset = stateForBadge === 'SUNSET';
        return {
            label: `${scopeWord} ${isSunset ? 'sunset' : 'suspended'}`,
            variant: isSunset ? 'sunset' : 'suspended',
            title: isSunset
                ? `This ${scopeWord.toLowerCase()} is sunset — content is read-only.`
                : `This ${scopeWord.toLowerCase()} is suspended — reactivate it to make changes.`,
        };
    }, [scopeFrozen, scopeGameState, scopeClientState]);

    // Super users browse all orgs — fetch the full org list (control-plane view).
    useEffect(() => {
        if (!isSuperUser || !user?.accessToken) return;
        getAllOrgs({ accessToken: user.accessToken })
            .then(d => setSuperAllOrgs(d?.orgs || []))
            .catch(err => console.error('Error fetching all orgs (super user):', err));
    }, [isSuperUser, user?.accessToken]);

    // Refresh the game lists when a game is registered (or builds change) elsewhere in the app —
    // and equally when a game/build's lifecycle transitions (suspend/activate/sunset/unsunset),
    // e.g. from Game Management. Without this, the switcher's catalogue (`allOrgGamesWithAccess`,
    // fetched once per org below) went stale until a hard refresh: the Activate/Suspend action
    // already broadcasts LIFECYCLE_CHANGED_EVENT (see notifyLifecycleChanged) for the CURRENTLY
    // SELECTED scope's own banner/lock-card, but the header switcher reads a separate org-wide
    // catalogue that wasn't listening for it.
    useEffect(() => {
        const onCatalogueStale = () => setGamesRefreshTick((t) => t + 1);
        // Back in touch with the server after an outage: the lists and names that failed meanwhile load again.
        const onReconnected = () => {
            setGamesRefreshTick((t) => t + 1);
            setOrgNamesRetryTick((t) => t + 1);
        };
        window.addEventListener(GAMES_CHANGED_EVENT, onCatalogueStale);
        window.addEventListener(LIFECYCLE_CHANGED_EVENT, onCatalogueStale);
        window.addEventListener(RECONNECTED_EVENT, onReconnected);
        return () => {
            window.removeEventListener(GAMES_CHANGED_EVENT, onCatalogueStale);
            window.removeEventListener(LIFECYCLE_CHANGED_EVENT, onCatalogueStale);
            window.removeEventListener(RECONNECTED_EVENT, onReconnected);
        };
    }, []);

    // Fetch user's game registries (for RBAC and game dropdown filtering)
    useEffect(() => {
        if (!user || !user.accessToken) return;
        const fetchRegistries = async () => {
            try {
                const data = await getUserGameRegistries({ accessToken: user.accessToken });
                setUserGameRegistries(data.userGameRegistryDTOS || []);
            } catch (error) {
                console.error('Error fetching user game registries:', error);
            } finally {
                setUserGameRegistriesLoaded(true);
            }
        };
        fetchRegistries();
    }, [user, gamesRefreshTick]);

    // Fetch the full org catalogue with accessibility flags whenever the selected org changes, and publish
    // it split three ways: the games the user can open (nav, switcher), the ones they are suspended on, and
    // the rest they may request (both shown only on the Games page).
    useEffect(() => {
        if (!user?.accessToken || !normalizedSelectedOrgId) {
            setAllOrgGamesWithAccess([]);
            dispatch(setUserGames([]));
            dispatch(setSuspendedGames([]));
            dispatch(setRequestableGames([]));
            // Signed in with no org → there is genuinely nothing to load; no token yet → still pending.
            dispatch(setUserGamesLoaded(!!user?.accessToken));
            catalogOrgRef.current = null;
            return;
        }
        // Org changed → drop the previous org's catalogue IMMEDIATELY so its games can't leak into
        // visibleOrgGames/uniqueGameNames during the (async) refetch. Otherwise the auto-select effect
        // briefly sees the old org's games and selects one under the newly-selected org — an
        // inconsistent (org, game) state that looks like the switch "snapping back" to the old org.
        // (Don't clear on a same-org refresh / gamesRefreshTick — that would needlessly flash empty.)
        if (catalogOrgRef.current && catalogOrgRef.current !== normalizedSelectedOrgId) {
            setAllOrgGamesWithAccess([]);
            dispatch(setUserGames([]));
            dispatch(setSuspendedGames([]));
            dispatch(setRequestableGames([]));
            dispatch(setUserGamesLoaded(false));
        }
        getGameRegistriesByOrgIdWithAccessibility({
            orgId: normalizedSelectedOrgId,
            accessToken: user.accessToken,
        })
            .then((resp) => {
                const items = (resp.games || []).map((item) => ({
                    ...item.game,
                    accessible: !!item.accessible,
                    // The server says the caller is suspended on this game (not merely never granted): the Games
                    // page shows it muted and its pages say so, instead of offering an access request.
                    suspended: !!item.suspended,
                    // Server ownership flag (Phase 3/4) — super-user / org owner-tier / this game's
                    // owner-tier. Sole source for the FE's "owned games" content filters.
                    owned: !!item.owned,
                    // Per-build accessibility from the server (B2) — sole source for build selectability.
                    // Left undefined on older payloads → treated as "no accessible builds" (safe default).
                    accessibleClientIds: Array.isArray(item.accessibleClientIds) ? item.accessibleClientIds : undefined,
                }));
                // This component keeps the FULL catalogue (owned-game filters, build resolution); the app-wide
                // userGames is only what this user can open. Everything that lists or resolves games from it -
                // the sidebar's Games list, the switcher, /game/<slug> pages, the onboarding banner and wizard -
                // would otherwise offer another member's games (e.g. someone else's DRAFT, straight into its wizard).
                setAllOrgGamesWithAccess(items);
                dispatch(setUserGames(items.filter(g => g.accessible)));
                dispatch(setSuspendedGames(items.filter(g => g.suspended && !g.accessible)));
                dispatch(setRequestableGames(requestableGames(items)));
                dispatch(setUserGamesLoaded(true));
                catalogOrgRef.current = normalizedSelectedOrgId;
            })
            .catch((err) => {
                console.error('Error fetching org games with accessibility:', err);
                // Settled either way, so a list page waiting on the catalogue stops loading.
                dispatch(setUserGamesLoaded(true));
            });
    }, [user?.accessToken, normalizedSelectedOrgId, gamesRefreshTick]);

    useEffect(() => {
        const onPortfoliosStale = () => setPortfoliosRefreshTick((t) => t + 1);
        window.addEventListener(PORTFOLIOS_CHANGED_EVENT, onPortfoliosStale);
        return () => window.removeEventListener(PORTFOLIOS_CHANGED_EVENT, onPortfoliosStale);
    }, []);

    // The signed-in user's own portfolios in the selected org — the Portfolio page, the sidebar list and
    // the portfolio switcher all read this. A role without portfolio access gets a 403 on first load,
    // which is "no portfolios", not an error; a failed refresh keeps the list already shown.
    useEffect(() => {
        if (!user?.accessToken || !normalizedSelectedOrgId) {
            dispatch(setUserPortfolios([]));
            // Signed in with no org → nothing to load; no token yet → still pending.
            dispatch(setUserPortfoliosLoaded(!!user?.accessToken));
            portfolioOrgRef.current = null;
            return undefined;
        }
        const orgId = normalizedSelectedOrgId;
        // Org changed → drop the previous org's portfolios at once rather than show them under this one.
        if (portfolioOrgRef.current && portfolioOrgRef.current !== orgId) {
            dispatch(setUserPortfolios([]));
            dispatch(setUserPortfoliosLoaded(false));
        }
        let cancelled = false;
        listPortfolios(orgId)
            .then((list) => {
                if (cancelled) return;
                const byName = [...list].sort(byPortfolioName);
                dispatch(setUserPortfolios(byName));
            })
            .catch(() => {
                if (!cancelled && portfolioOrgRef.current !== orgId) dispatch(setUserPortfolios([]));
            })
            .finally(() => {
                if (cancelled) return;
                dispatch(setUserPortfoliosLoaded(true));
                portfolioOrgRef.current = orgId;
            });
        return () => { cancelled = true; };
    }, [user?.accessToken, normalizedSelectedOrgId, portfoliosRefreshTick, dispatch]);

    // Fetch all games (for game details like builds, regions, clientIds)
    useEffect(() => {
        if (!user || !user.accessToken) return;

        const fetchGames = async () => {
            try {
                const gamesResponse = await getGameRegistries({
                    accessToken: user.accessToken
                });
                const normalizedGames = (gamesResponse.games || []).map(game => {
                    const normalizedOrgId = normalizeId(game.orgId);
                    const normalizedGameRegions = normalizeRegions(game.managedRegions);
                    const defaultClientId = normalizeId(game.gameClientId);
                    const groupClients = (game.builds || []).map(group => ({
                        gameClientId: normalizeId(group.gameClientId),
                        clientName: group.clientName,
                        managedRegions: normalizeRegions(group.managedRegions, group.includedCountries || normalizedGameRegions)
                    })).filter(client => !!client.gameClientId);

                    if (groupClients.length === 0 && defaultClientId) {
                        groupClients.push({
                            gameClientId: defaultClientId,
                            managedRegions: normalizedGameRegions
                        });
                    }

                    return {
                        ...game,
                        orgId: normalizedOrgId,
                        gameClientId: defaultClientId,
                        managedRegions: normalizedGameRegions,
                        clients: groupClients
                    };
                });
                setAllGames(normalizedGames);
            } catch (error) {
                console.error('Error fetching games:', error);
            }
        };

        fetchGames();
    }, [user, gamesRefreshTick]);

    // Fetch org names for all orgs the user belongs to. Keyed on the user, not the user object: that changes on every
    // hourly token rotation, and a refetch landing in a bad moment used to swap good names for raw ids. Names are
    // merged in (a failed lookup keeps the name already shown) and never fall back to the id - the switcher shows
    // "Organization" until the name loads, and a failed lookup is asked again (orgNames.js).
    const hasToken = !!user?.accessToken;
    useEffect(() => {
        if (!user || !user.accessToken) return undefined;

        // Super users: build names/data directly from the bulk all-orgs payload (no per-org calls).
        if (isSuperUser) {
            const names = {};
            const dataMap = {};
            superAllOrgs.forEach(o => {
                const id = normalizeId(o.id);
                if (!id) return;
                if (o.orgName) names[id] = o.orgName;
                dataMap[id] = orgDataOf(o);
            });
            setOrgNames(prev => ({ ...prev, ...names }));
            setOrgDataMap(prev => ({ ...prev, ...dataMap }));
            if (normalizedSelectedOrgId && names[normalizedSelectedOrgId]) {
                dispatch(updateDashboardContext({ selectedOrgName: names[normalizedSelectedOrgId] }));
            }
            return undefined;
        }

        if (orgs.length === 0) return undefined;

        let cancelled = false;
        let retryTimer = null;
        (async () => {
            const { names, dataMap, failed } = await fetchOrgNames(orgs, {
                getOrgById, getOrgNameById, accessToken: user.accessToken,
            });
            if (cancelled) return;
            setOrgNames(prev => ({ ...prev, ...names }));
            setOrgDataMap(prev => ({ ...prev, ...dataMap }));
            // Sync selectedOrgName to Redux if an org is already selected
            if (normalizedSelectedOrgId && names[normalizedSelectedOrgId]) {
                dispatch(updateDashboardContext({ selectedOrgName: names[normalizedSelectedOrgId] }));
            }
            if (failed.length > 0) {
                const delay = orgNameRetryDelay(orgNamesAttemptRef.current);
                orgNamesAttemptRef.current += 1;
                retryTimer = setTimeout(() => setOrgNamesRetryTick((t) => t + 1), delay);
            } else {
                orgNamesAttemptRef.current = 0;
            }
        })();
        return () => {
            cancelled = true;
            clearTimeout(retryTimer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgs, user?.uid, hasToken, isSuperUser, superAllOrgs, orgNamesRetryTick]);

    // --- Cascade: derive options from allGames + selectedOrgId ---

    // Games filtered by selected org
    const orgGames = useMemo(() => {
        if (!normalizedSelectedOrgId) return [];
        return allGames.filter(game => game.orgId === normalizedSelectedOrgId);
    }, [allGames, normalizedSelectedOrgId]);

    // User's game registry for the currently selected org (from user_game_registries)
    const userOrgGameRegistry = useMemo(() => {
        if (!normalizedSelectedOrgId || !userGameRegistries.length) return null;
        return userGameRegistries.find(ug => normalizeId(ug.orgId) === normalizedSelectedOrgId) || null;
    }, [userGameRegistries, normalizedSelectedOrgId]);

    // --- RBAC: coarse role for current org context ---
    // Return null until ALL required data has loaded so RoleProtectedRoute waits instead of redirecting.
    const orgDataLoaded = Object.keys(orgDataMap).length > 0;

    // Post Phase 4: `userRole` no longer carries owner-tier — its ONLY remaining job is the super-user
    // gate (route/nav via filterLinksByRole/getFirstAccessiblePath) and a "has the role settled yet?"
    // sentinel (GameUserManagement permsReady). Owner-tier UX now reads the explicit `isOrgOwner` /
    // `ownsAnyGame` flags below. See docs/access-role-derivation-migration.md.
    const userRole = useMemo(() => {
        if (isSuperUser) return 'SUPER_USER';
        if (!normalizedSelectedOrgId || !orgDataLoaded || !userGameRegistriesLoaded) return null;
        return 'REGULAR_USER';
    }, [isSuperUser, normalizedSelectedOrgId, orgDataLoaded, userGameRegistriesLoaded]);

    // Org owner-tier (owner OR co-owner) for the selected org. The authoritative signal is the
    // server-assigned org-level role on the user's registry (`invitationLevel`); the org doc
    // orgOwner/coOwners match is a case-insensitive belt-and-suspenders fallback for records where
    // invitationLevel hasn't propagated. This is the explicit successor to the old
    // `userRole === 'ORG_OWNER'` check — it never infers game-level ownership from invitationLevel.
    const isOrgOwner = useMemo(() => {
        if (!normalizedSelectedOrgId) return false;
        const orgLevel = userOrgGameRegistry?.invitationLevel;
        if (orgLevel === 'ORG_OWNER' || orgLevel === 'ORG_CO_OWNER') return true;
        const orgEntry = orgDataMap[normalizedSelectedOrgId];
        const matchesUser = (v) =>
            v != null && String(v).trim().toLowerCase() === String(userId || '').trim().toLowerCase();
        return !!orgEntry && (matchesUser(orgEntry.orgOwner) || (orgEntry.coOwners || []).some(matchesUser));
    }, [normalizedSelectedOrgId, userOrgGameRegistry, orgDataMap, userId]);

    // Owns ≥1 game in the org — straight off the server `owned` flag (super-user / org owner-tier own
    // every game; a game owner owns their own). Successor to the old `userRole === 'GAME_OWNER'` arm of
    // the owner-only UX gates.
    const ownsAnyGame = useMemo(
        () => allOrgGamesWithAccess.some(g => g.owned),
        [allOrgGamesWithAccess]
    );

    // Compute owned game names for the current org (used by page components to filter content).
    //
    // Phase 4 (server-only): derived entirely from the authoritative server `owned` flag — no
    // `userRole`, no `gameDetailsMap` inference. `null` is the "see all / no filtering" sentinel for
    // users who own every game in the org (super-user or org owner-tier — incl. co-owners, since the
    // server marks all of an org owner-tier's games owned). Everyone else gets the explicit list of
    // games they own. See docs/access-role-derivation-migration.md.
    const ownedGameNames = useMemo(() => {
        // Catalogue not loaded yet — don't over-filter; these pages are RBAC-gated to owners/admins.
        if (!allOrgGamesWithAccess.length) return null;
        if (isSuperUser) return null;
        // Owns every game in the org ⇒ org owner-tier ⇒ no filtering.
        if (allOrgGamesWithAccess.every(g => g.owned)) return null;
        return allOrgGamesWithAccess.filter(g => g.owned).map(g => g.gameName).filter(Boolean);
    }, [isSuperUser, allOrgGamesWithAccess]);

    // Compute owned game IDs and client IDs for the current org
    // (used to filter invitations whose gameDetailsMap keys are game IDs / client IDs).
    // Derived from ownedGameNames — inherits its `null` see-all sentinel.
    const ownedGameIds = useMemo(() => {
        if (ownedGameNames === null) return null;
        if (ownedGameNames.length === 0) return [];
        const ids = [];
        orgGames.forEach(game => {
            if (ownedGameNames.includes(game.gameName)) {
                if (game.id && !ids.includes(game.id)) ids.push(game.id);
                if (game.gameClientId && !ids.includes(game.gameClientId)) ids.push(game.gameClientId);
                // Also include build-level client IDs
                (game.clients || []).forEach(client => {
                    if (client.gameClientId && !ids.includes(client.gameClientId)) {
                        ids.push(client.gameClientId);
                    }
                });
            }
        });
        return ids;
    }, [orgGames, ownedGameNames]);

    // Get allowed domains for the selected org
    const allowedDomains = useMemo(() => {
        if (!normalizedSelectedOrgId || !orgDataMap[normalizedSelectedOrgId]) return [];
        return orgDataMap[normalizedSelectedOrgId].allowedDomains || [];
    }, [normalizedSelectedOrgId, orgDataMap]);

    // Dispatch computed role, owner-tier flags, owned games, and allowed domains to Redux
    useEffect(() => {
        dispatch(updateDashboardContext({ userRole, isOrgOwner, ownsAnyGame, ownedGameNames, ownedGameIds, allowedDomains }));
    }, [userRole, isOrgOwner, ownsAnyGame, ownedGameNames, ownedGameIds, allowedDomains, dispatch]);

    const permOrgRef = useRef(null);

    // Visible (selectable) games.
    //
    // Phase 1 of the access-role-derivation migration (docs/access-role-derivation-migration.md):
    // the SERVER's per-game `accessible` flag (`/with-accessibility`, in `allOrgGamesWithAccess`) is
    // the source of truth. It already cascades every owner tier — super-user, org owner AND co-owner,
    // game owner/co-owner, build co-owner (verified: DataScopeResolver maps an ORG owner-tier grant to
    // effectiveRole=ORG_OWNER → isAccessibleToCaller=true). Re-deriving this from `userRole` +
    // `gameDetailsMap` is exactly what mis-classified org co-owners and gated their game selection.
    //
    // Phase 4 (migration complete for this path): selectability IS the server `accessible` set, full
    // stop — the catalogue rows themselves (not an intersection with the user's own `allGames`/
    // `orgGames`). Intersecting with `orgGames` dropped games accessible only via an org-owner cascade
    // (e.g. an org owner's view of a game they hold no personal grant on): they'd show in the switcher
    // (built from the catalogue) yet be missing from `visibleOrgGames`/`uniqueGameNames`, so selecting
    // one couldn't resolve its id/name and the auto-select effect treated it as invalid. Sourcing
    // straight from the accessible catalogue keeps every selectable game fully resolvable. Until the
    // catalogue loads we show nothing (a sub-second empty switcher) — these pages are RBAC-gated anyway.
    const visibleOrgGames = useMemo(
        () => allOrgGamesWithAccess.filter(game => game.accessible),
        [allOrgGamesWithAccess]
    );

    // Unique game names for the selected org (filtered by user access).
    // Sorted by name so the list — and the auto-select fallback (uniqueGameNames[0]) — is
    // deterministic. The catalogue API returns games in no guaranteed order, so without this the
    // fallback picked a different "first" game on each refresh (the "random game" symptom).
    const uniqueGameNames = useMemo(() => {
        const names = [...new Set(visibleOrgGames.map(game => game.gameName))];
        names.sort((a, b) => String(a).localeCompare(String(b)));
        return names;
    }, [visibleOrgGames]);

    // Every build of the selected game, each tagged `accessible`. We list ALL builds (not just the
    // user's) so the switcher can show the ones they lack as a greyed "Request access" row — letting
    // someone with one client of a game request the others. The full build list comes from the org
    // catalogue (`allOrgGamesWithAccess`, which carries every game's raw builds[]); we fall back to
    // the data-scoped visible games only if the catalogue hasn't loaded yet.
    const gameClients = useMemo(() => {
        if (!selectedGameName) return [];

        // By id first: right after a rename the stored name is the old one until the selection catches up.
        const catalogGame = (selectedGameId && allOrgGamesWithAccess.find(g => normalizeId(g.id) === normalizeId(selectedGameId)))
            || allOrgGamesWithAccess.find(g => g.gameName === selectedGameName);
        let all = (catalogGame?.builds || [])
            .map(b => ({
                gameClientId: normalizeId(b.gameClientId),
                clientName: b.clientName,
                managedRegions: normalizeRegions(b.managedRegions, b.includedCountries),
                // Per-build lifecycle (ACTIVE / SUSPENDED / SUNSET) from the authoritative
                // game_clients rows — carried through so the switcher can flag a frozen build.
                state: b.state,
            }))
            .filter(c => !!c.gameClientId);
        if (all.length === 0) {
            all = visibleOrgGames
                .filter(game => game.gameName === selectedGameName)
                .flatMap(game => {
                    const sources = game.clients?.length > 0
                        ? game.clients
                        : [{ gameClientId: game.gameClientId, managedRegions: game.managedRegions }];
                    return sources.filter(c => c?.gameClientId);
                });
        }

        // Per-build accessibility (Phase 4 — server-only): the server's `accessibleClientIds` for this
        // game already cascades owner tiers + explicit build grants. No userRole/gameDetailsMap
        // re-derivation. A missing field (pre-B2 backend) yields an empty set → every build renders as
        // a "Request access" row, which is the safe (under-permissive) default.
        const serverBuildSet = new Set((catalogGame?.accessibleClientIds || []).map(normalizeId));

        // Builds the CURRENT user is suspended/archived on (their own membership state). A granted
        // build the user is suspended on is `accessible: true` (they hold the grant) but
        // `suspended: true` — it resolves to NO_ACCESS server-side, so auto-select and the switcher
        // must steer away from it rather than land the user there and bounce them to /not-authorized.
        // (Owners aren't suspendable, so this is empty for them anyway.)
        const suspendedIds = suspendedClientIdsForGame(
            userOrgGameRegistry, selectedGameName, normalizeId(selectedGameId));
        // Game-level lifecycle cascades into its builds (a build under a sunset/suspended game is
        // effectively frozen regardless of its own stored state — mirrors the backend guard).
        const gameState = catalogGame?.state;
        return all.map(c => {
            const effState = effectiveClientState(gameState, c.state);
            const frozen = !!effState && effState !== GAME_STATE.ACTIVE;
            return {
                ...c,
                accessible: serverBuildSet.has(normalizeId(c.gameClientId)),
                suspended: suspendedIds.has(normalizeId(c.gameClientId)),
                // Effective lifecycle when frozen (else null). Drives the switcher's Sunset/Suspended
                // pill and blocks selecting a terminal (sunset) build. Distinct from `suspended`,
                // which is the CURRENT user's membership suspension on the build.
                lifecycleState: frozen ? effState : null,
            };
        });
    }, [visibleOrgGames, selectedGameName, selectedGameId, allOrgGamesWithAccess, userOrgGameRegistry]);

    // Re-fetch effective permissions only when the org/game context actually changes.
    // userRole is excluded from the key — the server resolves the role internally, and
    // including it causes spurious refetches because userRole is unstable on remount
    // (starts as REGULAR_USER until orgDataMap loads async). Declared after `gameClients`
    // because it reads that memo to steer clear of suspended builds.
    useEffect(() => {
        if (!user?.accessToken || !normalizedSelectedOrgId) return;
        // Wait until we know the user's per-build access before resolving permissions. Resolving
        // for a build the user is suspended on returns NO_ACCESS and bounces them to the standalone
        // /not-authorized page even when they have OTHER usable builds — and that page can't
        // recover the context. Once registries load, the build auto-select effect switches to a
        // usable build and this effect re-runs for it. Super users have no per-build grants to wait
        // on. While we wait, effectivePermissions stays null → guards render their loading state.
        if (!isSuperUser && !userGameRegistriesLoaded) return;
        // "Unusable" selected build = the user is suspended on it, OR it's terminally sunset (a
        // dead-end the auto-select effect steers away from). Either way, if a usable build exists we
        // hold perms while the switch lands rather than resolving for a build we're about to leave.
        const selBuildUnusable = gameClients.some(
            g => normalizeId(g.gameClientId) === normalizedSelectedGameClientId
                && (g.suspended || g.lifecycleState === 'SUNSET'));
        const hasUsableBuild = gameClients.some(
            g => g.accessible && !g.suspended && g.lifecycleState !== 'SUNSET');
        if (selBuildUnusable && hasUsableBuild) {
            // The active build is unusable (suspended for this user, or sunset), but a usable build
            // exists — the auto-select effect is about to switch to it. Hold permissions in the
            // loading state (rather than resolving for the build we're leaving) so guards wait for
            // the switch instead of flashing the lock card.
            if (effectivePermissions !== null) dispatch(setEffectivePermissions(null));
            return;
        }
        // Keyed on org + game only — the scope the backend actually resolves against
        // (getEffectivePermissions(userId, orgId, gameId)). Including the client id made every build
        // switch refetch permissions that cannot differ, and replacing effectivePermissions rebuilds
        // the nav and re-runs the redirect guard for nothing.
        const contextKey = `${normalizedSelectedOrgId}|${selectedGameId}|${selectedSegmentId || ''}`;
        if (sessionStorage.getItem('permissionsContextKey') === contextKey) return;
        sessionStorage.setItem('permissionsContextKey', contextKey);
        // ORG changed → the cached permissions belong to the previous org. Clear them so guards
        // (usePermission → null) render their loading state instead of acting on stale access —
        // this is what stops a flash of the dashboard when switching INTO a suspended org before
        // its NO_ACCESS resolves. (Game/client switches within an org keep perms to avoid flicker.)
        if (permOrgRef.current && permOrgRef.current !== normalizedSelectedOrgId) {
            dispatch(setEffectivePermissions(null));
        }
        permOrgRef.current = normalizedSelectedOrgId;
        // The backend resolves effective permissions from the user's STORED active context, not from
        // params on this call — so we must persist the just-selected context BEFORE fetching, or perms
        // come back for the PREVIOUS context. (The debounced persist effect below fires 500ms later,
        // which lost the race on a quick game switch: perms resolved against the old game, the current
        // page came back NO_ACCESS, and DashboardLayout bounced the user to Profile with an empty nav.)
        // Persist failure shouldn't block the fetch — fall through and resolve against whatever's stored.
        updateActiveContext({
            accessToken: user.accessToken,
            activeOrgId: normalizedSelectedOrgId,
            activeGameId: selectedGameId,
            activeClientId: normalizedSelectedGameClientId,
        })
            .catch(err => console.error('Failed to persist active context before resolving permissions:', err))
            .then(() => getEffectivePermissions({ accessToken: user.accessToken }))
            .then(perms => dispatch(setEffectivePermissions(perms)))
            .catch(err => console.error('Failed to fetch effective permissions:', err));
    }, [user?.accessToken, normalizedSelectedOrgId, selectedGameId, normalizedSelectedGameClientId, dispatch, isSuperUser, userGameRegistriesLoaded, gameClients, effectivePermissions, selectedSegmentId]);

    // --- Load the game's active GEO segments for the header segment picker ---
    // A failed read stays unknown (null), not "no whole-game access", and is read again when the token changes: a 401
    // before the sign-in was restored left a whole-game owner refused on game-wide pages for the rest of the session.
    useEffect(() => {
        const gameChanged = prevSegmentGameRef.current !== selectedGameId;
        prevSegmentGameRef.current = selectedGameId;
        if (gameChanged && selectedSegmentId) dispatch(updateDashboardContext({ selectedSegmentId: null }));
        if (!selectedGameId) { setMyGeographies(null); return; }
        if (!gameChanged && myGeographies) return;
        setMyGeographies(null);
        let cancelled = false;
        getMyGeographies(selectedGameId, { keepOnError: true }).then(mine => { if (!cancelled) setMyGeographies(mine); });
        return () => { cancelled = true; };
    }, [selectedGameId, selectedSegmentId, user?.accessToken, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

    // Re-read them when this game's segments change (one added, edited or deleted on the Segments page) or the
    // caller's access changes (a geography granted or removed): they were loaded once per game, so a new
    // geography only appeared after switching games or reloading (reported from QA). No clearing first, so the
    // switcher does not flash empty; reconcile below drops a selection that is no longer offered.
    useEffect(() => {
        if (!selectedGameId) return undefined;
        let cancelled = false;
        const reload = (event) => {
            if (event?.type === SEGMENTS_CHANGED_EVENT && !concernsGame(event, selectedGameId)) return;
            getMyGeographies(selectedGameId, { keepOnError: true }).then(mine => { if (!cancelled && mine) setMyGeographies(mine); });
        };
        window.addEventListener(SEGMENTS_CHANGED_EVENT, reload);
        window.addEventListener(ACCESS_CHANGED_EVENT, reload);
        return () => {
            cancelled = true;
            window.removeEventListener(SEGMENTS_CHANGED_EVENT, reload);
            window.removeEventListener(ACCESS_CHANGED_EVENT, reload);
        };
    }, [selectedGameId]);

    // Shared with the page header's scope pill: the selected geography's name, and whether the caller's access
    // covers the whole game (a geography-only member sees game-wide pages read-only).
    useEffect(() => {
        const name = selectedSegmentId
            ? (geographyPickerItems(myGeographies).find(s => s.value === selectedSegmentId)?.label || null)
            : null;
        dispatch(updateDashboardContext({
            selectedSegmentName: name,
            wholeGameAccess: myGeographies ? !!myGeographies.wholeGame : null,
        }));
    }, [myGeographies, selectedSegmentId, dispatch]);

    // A geography-limited user has nothing in the Global view: select their first geography for them (and
    // drop a selection the user can no longer see). Whole-game users keep Global as the default.
    useEffect(() => {
        if (!myGeographies) return;
        const next = reconcileSelectedSegment(myGeographies, selectedSegmentId);
        if ((next || null) !== (selectedSegmentId || null)) {
            dispatch(updateDashboardContext({ selectedSegmentId: next }));
        }
    }, [myGeographies, selectedSegmentId, dispatch]);

    // --- Resolve gameName from gameId after login (backend stores gameId, not gameName) ---
    // Look in the user's own games first, then the server accessibility catalogue — a game accessible
    // only via an org-owner cascade isn't in `allGames`, so without the catalogue its name never
    // resolves and the switcher chip stays the generic "Game" even though the page loaded it.
    useEffect(() => {
        if (!selectedGameId || selectedGameName) return;
        const match = allGames.find(g => normalizeId(g.id) === normalizeId(selectedGameId))
            || allOrgGamesWithAccess.find(g => normalizeId(g.id) === normalizeId(selectedGameId));
        if (match?.gameName) {
            dispatch(updateDashboardContext({ selectedGameName: match.gameName }));
        }
    }, [allGames, allOrgGamesWithAccess, selectedGameId, selectedGameName, dispatch]);

    // --- Auto-selection cascades ---

    // Helper: compute clients for a given game name. Prefer visibleOrgGames, but fall back to the
    // server accessibility catalogue — a game accessible purely via an org-owner cascade (e.g. an
    // org owner opening a game they don't personally hold a grant on) is in `allOrgGamesWithAccess`
    // but NOT in `allGames`/`orgGames`, so resolving only from visibleOrgGames would find nothing and
    // the game-switch would silently no-op. The catalogue rows carry `builds` rather than `clients`.
    const getClientsForGame = (gameName) => {
        const matches = visibleOrgGames.filter(game => game.gameName === gameName);
        const source = matches.length
            ? matches
            : allOrgGamesWithAccess.filter(game => game.gameName === gameName);
        return source.flatMap(game => {
            const sources = game.clients?.length > 0
                ? game.clients
                : (game.builds?.length > 0
                    ? game.builds
                    : [{ gameClientId: game.gameClientId, managedRegions: game.managedRegions }]);
            return sources.filter(c => c?.gameClientId);
        });
    };

    // Client builds of an ARBITRARY game the CURRENT user can actually OPEN — same accessibility logic
    // as the `gameClients` memo, but the memo only covers the selected game. Used to land a top-bar game
    // switch on a build the user owns/holds, not the game's first build (which a build co-owner may NOT
    // own — that was navigating them into an inaccessible build on switch and bouncing them out).
    const accessibleClientIdsForGame = (gameName, gameId) => {
        const catalogGame = allOrgGamesWithAccess.find(g => g.gameName === gameName);
        const selId = normalizeId(gameId);
        // Builds the user is suspended on always come off the candidate set — opening one resolves
        // NO_ACCESS, so we must never land there.
        const suspended = suspendedClientIdsForGame(userOrgGameRegistry, gameName, selId);
        // Sunset builds are terminal dead-ends — also never a landing target on a game switch.
        // (Honors game containment: every build of a sunset game is effectively sunset.)
        const sunset = new Set(
            (catalogGame?.builds || [])
                .filter(b => effectiveClientState(catalogGame?.state, b.state) === GAME_STATE.SUNSET)
                .map(b => normalizeId(b.gameClientId)));

        // Phase 4 (server-only): the server's per-build accessibility, owner-cascade already applied.
        // FE only layers its suspended-/sunset-build filters on top. A missing field yields no
        // landable builds (the switcher then falls back to its own handling) — no userRole/
        // gameDetailsMap re-derivation.
        return (catalogGame?.accessibleClientIds || [])
            .map(normalizeId)
            .filter(Boolean)
            .filter(id => !suspended.has(id) && !sunset.has(id));
    };

    // Helper: get the game registry id for a given game name. Falls back to the server accessibility
    // catalogue so a game accessible only via an org-owner cascade (present in allOrgGamesWithAccess
    // but not allGames/orgGames) still resolves — otherwise clicking it in the switcher resolves a
    // null gameId and nothing happens.
    const getGameIdForName = (gameName) => {
        const match = visibleOrgGames.find(game => game.gameName === gameName)
            || allOrgGamesWithAccess.find(game => game.gameName === gameName);
        return match?.id ? normalizeId(match.id) : null;
    };

    // ── Phase-0 guardrail (DEV only): measure drift between the server's authoritative per-game
    // `accessible` flag and what the userRole-derived path can actually select. Quantifies the tech
    // debt we're removing and flags regressions while the migration lands. See
    // docs/access-role-derivation-migration.md. Pure no-op in production.
    useEffect(() => {
        if (!import.meta.env?.DEV || !allOrgGamesWithAccess.length) return;
        const drift = detectAccessDrift({
            serverAccessibleGames: allOrgGamesWithAccess,
            feSelectable: (gameName) => {
                const inVisible = visibleOrgGames.some(v => v.gameName === gameName);
                const gameId = getGameIdForName(gameName);
                const landableBuilds = gameId ? accessibleClientIdsForGame(gameName, gameId).length : 0;
                return { inVisible, landableBuilds };
            },
        });
        logAccessDrift({ orgId: normalizedSelectedOrgId, userRole, drift });
        // getGameIdForName / accessibleClientIdsForGame are recreated each render; intentionally
        // excluded so this only re-checks when the underlying access data changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allOrgGamesWithAccess, visibleOrgGames, userRole, normalizedSelectedOrgId]);

    // Auto-select org if only 1 (uses effectiveOrgs so super users with a single platform org also
    // auto-select; with many orgs nothing auto-selects and they pick from the switcher).
    useEffect(() => {
        if (effectiveOrgs.length === 1 && !normalizedSelectedOrgId) {
            dispatch(updateDashboardContext({ selectedOrgId: String(effectiveOrgs[0].orgId) }));
        }
    }, [effectiveOrgs, normalizedSelectedOrgId, dispatch]);

    // When org changes or games load: auto-select first game (and first build)
    useEffect(() => {
        // Wait until the accessibility catalogue for the CURRENT org has loaded before clearing or
        // auto-selecting. The catalogue (which drives uniqueGameNames) is fetched async and is briefly
        // empty on every refresh; acting in that window wipes the just-restored selection and then
        // auto-picks an arbitrary first game — the "random game on refresh" bug. catalogOrgRef is set
        // to the org id only after its catalogue resolves, so this doubles as a cross-org-leak guard.
        if (catalogOrgRef.current !== normalizedSelectedOrgId) return;

        if (uniqueGameNames.length === 0) {
            if (selectedGameName) {
                dispatch(updateDashboardContext({
                    selectedGameId: null,
                    selectedGameName: null,
                    selectedGameClientId: null
                }));
            }
            return;
        }
        // If no game selected or current selection is not valid for this org, select first game
        if (!selectedGameName || !uniqueGameNames.includes(selectedGameName)) {
            // A renamed game is still the selected one: keep it, under its new name, rather than jump to the first game.
            const renamed = selectedGameId && visibleOrgGames.find(g => normalizeId(g.id) === normalizeId(selectedGameId));
            if (renamed?.gameName) {
                dispatch(updateDashboardContext({ selectedGameName: renamed.gameName }));
                return;
            }
            const firstGame = uniqueGameNames[0];
            const clients = getClientsForGame(firstGame);
            dispatch(updateDashboardContext({
                selectedGameId: getGameIdForName(firstGame),
                selectedGameName: firstGame,
                selectedGameClientId: clients.length > 0 ? clients[0].gameClientId : null
            }));
        }
    }, [uniqueGameNames, selectedGameName, selectedGameId, dispatch, visibleOrgGames, allGames, normalizedSelectedOrgId]);

    // When game name changes: auto-select first game client
    useEffect(() => {
        // Same catalogue-load guard as the game auto-select above: gameClients is derived from the
        // async catalogue and is empty until it loads, so acting now would null the restored build and
        // then auto-pick an arbitrary one (the random build on refresh). Wait for the current org's
        // catalogue before clearing/auto-selecting.
        if (catalogOrgRef.current !== normalizedSelectedOrgId) return;

        // "Usable" = granted, not suspended for this user, AND not sunset. A build the user is
        // suspended on is never auto-selected (it resolves to NO_ACCESS and would bounce them to
        // /not-authorized); a sunset build is a terminal dead-end (RequireActiveScope locks it with
        // no recovery), so we prefer never to auto-land there. Nor is one they can only request access to.
        const usableClients = gameClients.filter(
            g => g.accessible && !g.suspended && g.lifecycleState !== 'SUNSET');
        // Fall back to any granted (non-membership-suspended) build when there's no usable one — e.g.
        // a fully-sunset game — so the build chip still reflects the selected scope (and the page shows
        // the sunset lock) rather than emptying the chip entirely.
        const accessibleClients = usableClients.length > 0
            ? usableClients
            : gameClients.filter(g => g.accessible && !g.suspended);
        if (accessibleClients.length === 0) {
            if (normalizedSelectedGameClientId) {
                dispatch(updateDashboardContext({ selectedGameClientId: null }));
            }
            return;
        }
        const clientIds = accessibleClients.map(g => g.gameClientId);
        // If no build is selected or the current one is invalid/inaccessible/suspended, switch to
        // the first usable build.
        if (!normalizedSelectedGameClientId || !clientIds.includes(normalizedSelectedGameClientId)) {
            dispatch(updateDashboardContext({ selectedGameClientId: accessibleClients[0].gameClientId }));
        }
    }, [gameClients, normalizedSelectedGameClientId, dispatch, allGames, normalizedSelectedOrgId]);

    // --- Persist active context to backend (debounced) ---
    useEffect(() => {
        if (!user?.accessToken) return;
        if (!selectedOrgId && !selectedGameId && !selectedGameClientId) return;

        clearTimeout(persistTimerRef.current);
        persistTimerRef.current = setTimeout(() => {
            updateActiveContext({
                accessToken: user.accessToken,
                activeOrgId: selectedOrgId,
                activeGameId: selectedGameId,
                activeClientId: selectedGameClientId,
            }).catch(err => console.error('Failed to persist active context:', err));
        }, 500);

        return () => clearTimeout(persistTimerRef.current);
    }, [selectedOrgId, selectedGameId, selectedGameClientId, user?.accessToken]);

    // --- Handlers ---

    const handleOrgChange = (e) => {
        const orgId = e.target.value;
        dispatch(updateDashboardContext({
            selectedOrgId: orgId,
            selectedOrgName: orgNames[orgId] || null,
            selectedGameId: null,
            selectedGameName: null,
            selectedGameClientId: null
        }));
        // The Game Workspace is URL-pinned to a specific build (`/games/:gameClientId/...`) of the
        // CURRENT org, and re-syncs redux FROM that URL (incl. selectedOrgId = the build's org). Staying
        // on that URL after switching org snaps the org selection right back. Leave it for the workspace
        // PICKER instead of Home — it keeps the user in the workspace flow: it redirects straight into
        // the new org's auto-selected (or only) game's workspace, or shows the game grid / empty state
        // when the new org has several / no accessible games.
        if (isGameWorkspace) {
            // Preserve the workspace tab across the org switch: the picker reads `:tab` from the URL
            // and redirects the resolved new-org game into `/games/:clientId/<tab>`. Without the tab
            // segment it defaults to setup, dropping the operator off their current tab.
            const m = location.pathname.match(/^\/admin-settings\/game-mgmt\/games\/[^/]+\/([^/]+)/);
            const tab = m ? m[1] : '';
            navigate(tab
                ? `/admin-settings/game-mgmt/workspace/${tab}`
                : '/admin-settings/game-mgmt/workspace');
        }
    };

    // --- Switcher cards ---

    const orgOptions = effectiveOrgs
        .filter(org => orgNames[org.orgId])
        .map(org => ({
            value: org.orgId,
            label: orgNames[org.orgId],
            // A suspended / archived org: a pill, but still selectable (see orgStatusLabel).
            statusLabel: orgStatusLabel(orgDataMap?.[org.orgId]?.orgState),
        }));

    const orgCard = {
        key: 'org',
        label: 'Organization',
        pluralLabel: 'orgs',
        singular: 'org',
        tone: 'org',
        items: orgOptions,
        selectedValue: normalizedSelectedOrgId || '',
        selectedLabel: orgNames[normalizedSelectedOrgId] || '',
        onSelect: (value) => handleOrgChange({ target: { value } }),
    };

    // The selected game's own record (icon, lifecycle, description, ownership) — the catalogue the
    // switcher already loads, so the rich chip costs no request.
    const selectedGameRecord = (Array.isArray(userGames) ? userGames : [])
        .find(g => normalizeId(g?.id) === normalizeId(selectedGameId)) || null;
    // What the user can do on THIS page (Read / Write, Read Only), or no badge on a page without a permission
    // of its own; unknown while permissions load. See pageAccess.js.
    const gameAccessLabel = selectedGameRecord
        ? pageAccessLabel({ pathname: location.pathname, effectivePermissions, isSuperUser })
        : null;
    const gameChipExtras = selectedGameRecord ? (
        <>
            {selectedGameRecord.state && <StatusPill size="small" status={selectedGameRecord.state} icon="auto" />}
            {gameAccessLabel && (
                <StatusPill
                    size="small"
                    tone={gameAccessLabel === READ_WRITE ? 'info' : 'neutral'}
                    icon={gameAccessLabel === READ_WRITE ? faPenToSquare : faEye}
                >
                    {gameAccessLabel}
                </StatusPill>
            )}
            {selectedGameRecord.gameDescription && (
                <span className="ctx-game-description">{selectedGameRecord.gameDescription}</span>
            )}
        </>
    ) : null;

    // How many of the org's other games the Games page offers to request - said in the switcher's footer link.
    const requestableCount = requestableGames(allOrgGamesWithAccess).length;

    const gameCard = {
        key: 'game',
        label: 'Game',
        pluralLabel: 'games',
        singular: 'game',
        tone: 'game',
        // The games this user can open - the sidebar's set. The org's other games, and the way to ask for
        // one, are on the Games page, which the list's footer links to (switcherGames.js).
        items: switcherGames(allOrgGamesWithAccess)
            .map(g => ({
                value: gameSlug(g),
                label: g.gameName || g.id,
                // Shown beside the name, copyable: the id the SDK, the API and support all ask for.
                copyId: g.id,
                copyIdLabel: 'Game ID',
                // Suspended / Sunset pill, and not selectable: its pages are locked until it is reactivated.
                lifecycleState: lifecycleStateOf(g),
            })),
        footerLink: {
            label: 'Browse all games',
            hint: requestableCount > 0 ? `${requestableCount} more you can request` : null,
            onClick: () => navigate('/game'),
        },
        selectedValue: selectedGameId ? gameSlug({ id: selectedGameId, gameName: selectedGameRecord?.gameName ?? selectedGameName }) : '',
        selectedLabel: selectedGameName || '',
        // Same destination as the sidebar and the Games grid: a DRAFT game resumes its setup wizard.
        onSelect: (value) => {
            const game = findGameBySlug(userGames, value);
            navigate(game ? gameEntryPath(game) : `/game/${value}`);
        },
        // In the bar: the game's icon tile, a bolder name, its status, your access and its description.
        rich: true,
        // The shared GameIcon: the game's icon when it has one (initials when not, or when the image
        // fails to load). No `state`: the status pill beside it already says Draft.
        renderChipBadge: (label) => (
            <span className="ctx-tile" aria-hidden="true">
                <GameIcon iconUrl={selectedGameRecord?.gameIconUrl} name={label} size={26} />
            </span>
        ),
        chipExtras: gameChipExtras,
    };

    const segmentItems = geographyPickerItems(myGeographies);
    // A game-wide page (Player Attributes, the game's settings, ...): its data is the whole game's, so the picker reads
    // "Whole game" and does not open - the selected geography is kept for the pages that use it. Decided 9 October 2026.
    const gameWidePage = gamePageScope(location.pathname) === 'game';
    const gameWideTooltip = `${gamePageLabel(location.pathname) || 'This page'} applies to every player of `
        + `${selectedGameName || 'this game'}, whichever geography you are viewing. Your geography is kept for the `
        + 'pages that use it.';
    const segmentCard = {
        key: 'segment',
        // It lists geographies (GEO segments) only - the views access is granted on - so it says so. Labelled
        // "Segment" / "All segments", it read as if every segment belonged here, and an audience segment
        // added on the Segments page looked like it had failed to appear (reported from QA).
        label: 'Geography',
        pluralLabel: 'geographies',
        singular: 'geography',
        tone: 'segment',
        items: segmentItems,
        selectedValue: selectedSegmentId || GLOBAL_VALUE,
        selectedLabel: segmentItems.find(s => s.value === (selectedSegmentId || GLOBAL_VALUE))?.label || GLOBAL_ITEM.label,
        onSelect: (value) => dispatch(updateDashboardContext({ selectedSegmentId: (value && value !== GLOBAL_VALUE) ? value : null })),
        // The bar's geography picker: a globe and the geography, "All players" for the global view.
        renderChipBadge: () => (
            <span className="ctx-tile" aria-hidden="true">
                <FontAwesomeIcon icon={faEarthAmericas} className="ctx-picker-icon" />
            </span>
        ),
        chipLabel: gameWidePage
            ? 'Whole game'
            : selectedSegmentId
                ? (segmentItems.find(s => s.value === selectedSegmentId)?.label || undefined)
                : 'All players',
        chipTooltip: gameWidePage ? gameWideTooltip : undefined,
    };

    const activePortfolio = findPortfolioBySlug(userPortfolios, routePortfolioSlug(location.pathname));
    const portfolioCard = {
        key: 'portfolio',
        label: 'Portfolio',
        pluralLabel: 'portfolios',
        singular: 'portfolio',
        tone: 'org',
        items: (Array.isArray(userPortfolios) ? userPortfolios : [])
            .filter(p => p && p.id)
            .map(p => ({ value: portfolioSlug(p), label: p.name || p.id })),
        selectedValue: activePortfolio ? portfolioSlug(activePortfolio) : '',
        selectedLabel: activePortfolio?.name || '',
        onSelect: (value) => navigate(`/portfolio/${value}`),
    };

    // A geo segment narrows ONE game's players, so with no game selected there is nothing for it to
    // narrow. The card still rendered, and since "Global (all players)" is a synthetic always-present
    // default rather than anything the server granted, it showed a ticked row reading "1 total" — which
    // looks like access to a segment for a user who has access to no game at all.
    const cardsByKey = {
        // In the bar the org leads quietly (no badge); the sidebar's org switcher keeps orgCard as is.
        org: { ...orgCard, muted: true, renderChipBadge: () => null },
        game: gameCard,
        segment: selectedGameId ? segmentCard : null,
        portfolio: portfolioCard,
    };
    const headerCards = headerCardsForPath(scope.id, location.pathname).map(key => cardsByKey[key]).filter(Boolean);
    // The geography is a picker of its own on the right (Nitro Play style); the rest is the context.
    const contextCards = headerCards.filter(c => c.key !== 'segment');
    const geographyCards = headerCards.filter(c => c.key === 'segment');
    // The game chip shows the game's own lifecycle, so the separate badge is only for a frozen BUILD.
    const gameStateShown = contextCards.some(c => c.key === 'game') && !!selectedGameRecord?.state;
    const showScopeBadge = scopeBadge
        && !(gameStateShown && scopeGameState && scopeGameState !== 'ACTIVE');

    return (
        <header className="header-container">
            <div className="header">
                <div className="header-left">
                    {!hideContextSwitcher && contextCards.length > 0 && (
                        <ContextSwitcher
                            cards={contextCards}
                            separator="chevron"
                            disabled={isFormPage || isLockedContextPage(location.pathname)}
                        />
                    )}
                    {/* The org switcher lives in the nav rail. Rendered here (and portalled into the
                        sidebar's slot) so it shares this component's org catalogue and the one
                        handleOrgChange — which resets game/client, persists the active context and
                        steers off URL-pinned workspace routes. */}
                    {scope.id !== 'admin' && (
                        <SidebarSwitcher>
                            <ContextSwitcher
                                variant="sidebar"
                                honorOpenRequest={false}
                                cards={[orgCard]}
                                disabled={isFormPage || isLockedContextPage(location.pathname)}
                            />
                        </SidebarSwitcher>
                    )}
                    {!hideContextSwitcher && isSelectedGameStatusRelevant(effectivePath) && showScopeBadge && (
                        <span
                            className={`scope-status-badge scope-status-badge--${scopeBadge.variant}`}
                            title={scopeBadge.title}
                            role="status"
                        >
                            <span className="scope-status-badge__dot" aria-hidden="true" />
                            {scopeBadge.label}
                        </span>
                    )}
                </div>
                <div className="header-right">
                    {!hideContextSwitcher && geographyCards.length > 0 && (
                        <ContextSwitcher
                            cards={geographyCards}
                            variant="picker"
                            honorOpenRequest={false}
                            disabled={isFormPage || isLockedContextPage(location.pathname) || gameWidePage}
                        />
                    )}
                    {isSuperUser && (
                        <span className="super-user-badge" title="Platform Super User — control-plane access">
                            <FontAwesomeIcon icon={faUserShield} />
                            Super User
                        </span>
                    )}
                    {/* Page-supplied actions (filters, page menus, etc.), then the
                     * bell — which stays rightmost so notifications always occupy
                     * that position. The AI assistant launcher that used to sit
                     * between them is archived in src/old/milestone2. */}
                    <HeaderActionsSlot className="header-right-container" />
                    <NotificationBell />
                </div>
            </div>
        </header>
    );
};
